import { useState } from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Gamepad, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Default deck URL for card images
const DEFAULT_DECK_URL = 'https://teacherwilliam.sgp1.cdn.digitaloceanspaces.com/spar/deck/free/default';
// App logo URL
const APP_LOGO_URL = 'https://teacherwilliam.sgp1.cdn.digitaloceanspaces.com/spar/spar-legends.jpeg';

interface GameExplanationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onStartPractice: () => void;
  onSkip: () => void;
  defaultDeckUrl?: string; // Make this optional since we have a default value now
}

export function GameExplanationDialog({
  isOpen,
  onOpenChange,
  onStartPractice,
  onSkip,
  defaultDeckUrl = DEFAULT_DECK_URL // Use the default if not provided
}: GameExplanationDialogProps) {
  const [currentPage, setCurrentPage] = useState(0);
  
  const handleNext = () => {
    if (currentPage < 4) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const handleBack = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Animation variants for page transitions
  const pageVariants = {
    hidden: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    }),
  };

  // Direction of page transition (1 for forward, -1 for backward)
  const [direction, setDirection] = useState(0);

  const nextPage = () => {
    if (currentPage < 4) {
      setDirection(1);
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setDirection(-1);
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black/95 border border-primary/30 shadow-lg max-w-md">
        <DialogHeader className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full overflow-hidden mb-4 border-2 border-primary/50 shadow-glow">
            <img 
              src={APP_LOGO_URL} 
              alt="Spar Legends" 
              className="w-full h-full object-cover"
            />
          </div>
          <DialogTitle className="text-xl text-center">
            How to Play Spar Legends
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          {/* Page content with animations */}
          <AnimatePresence custom={direction} initial={false} mode="wait">
            {currentPage === 0 && (
              <motion.div 
                key="page0"
                custom={direction}
                variants={pageVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="min-h-[280px]"
              >
                <h3 className="text-lg font-bold mb-4 text-center">The Suits</h3>
                <p className="mb-4 text-sm text-center">
                  There are four suits used in the game:
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Kings and sixes for each suit */}
                  <div className="flex flex-col items-center">
                    <div className="flex gap-2 mb-2">
                      <img src={`${defaultDeckUrl}/A13.svg`} alt="A13" className="w-16 h-24" />
                      <img src={`${defaultDeckUrl}/A6.svg`} alt="A6" className="w-16 h-24" />
                    </div>
                    <p className="text-sm text-center">Spades</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="flex gap-2 mb-2">
                      <img src={`${defaultDeckUrl}/B13.svg`} alt="B13" className="w-16 h-24" />
                      <img src={`${defaultDeckUrl}/B6.svg`} alt="B6" className="w-16 h-24" />
                    </div>
                    <p className="text-sm text-center">Hearts</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="flex gap-2 mb-2">
                      <img src={`${defaultDeckUrl}/C13.svg`} alt="C13" className="w-16 h-24" />
                      <img src={`${defaultDeckUrl}/C6.svg`} alt="C6" className="w-16 h-24" />
                    </div>
                    <p className="text-sm text-center">Clubs</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="flex gap-2 mb-2">
                      <img src={`${defaultDeckUrl}/D13.svg`} alt="D13" className="w-16 h-24" />
                      <img src={`${defaultDeckUrl}/D6.svg`} alt="D6" className="w-16 h-24" />
                    </div>
                    <p className="text-sm text-center">Diamonds</p>
                  </div>
                </div>
              </motion.div>
            )}

            {currentPage === 1 && (
              <motion.div 
                key="page1"
                custom={direction}
                variants={pageVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="min-h-[280px]"
              >
                <h3 className="text-lg font-bold mb-4 text-center">Same Suit Ranking</h3>
                <p className="mb-6 text-sm text-center">
                  When cards of the same suit are played, the higher card wins.
                </p>
                
                <div className="flex justify-center gap-4 mb-6">
                  <div className="flex flex-col items-center">
                    <img src={`${defaultDeckUrl}/A12.svg`} alt="A12" className="w-24 h-36 mb-2" />
                    <p className="text-sm">Queen of Spades</p>
                  </div>
                  
                  <div className="flex items-center">
                    <span className="text-xl font-bold mx-2 text-yellow-500">VS</span>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <img src={`${defaultDeckUrl}/A13.svg`} alt="A13" className="w-24 h-36 mb-2" />
                    <p className="text-sm">King of Spades</p>
                  </div>
                </div>
                
                <p className="text-sm text-center font-medium text-green-500">
                  King beats Queen since they're the same suit.
                </p>
              </motion.div>
            )}

            {currentPage === 2 && (
              <motion.div 
                key="page2"
                custom={direction}
                variants={pageVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="min-h-[280px]"
              >
                <h3 className="text-lg font-bold mb-4 text-center">Different Suits</h3>
                <p className="mb-6 text-sm text-center">
                  Cards of different suits cannot be compared against each other.
                </p>
                
                <div className="flex justify-center gap-4 mb-6">
                  <div className="flex flex-col items-center">
                    <img src={`${defaultDeckUrl}/B10.svg`} alt="B10" className="w-24 h-36 mb-2" />
                    <p className="text-sm">10 of Hearts</p>
                  </div>
                  
                  <div className="flex items-center">
                    <span className="text-xl font-bold mx-2 text-yellow-500">VS</span>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <img src={`${defaultDeckUrl}/C6.svg`} alt="C6" className="w-24 h-36 mb-2" />
                    <p className="text-sm">6 of Clubs</p>
                  </div>
                </div>
                
                <p className="text-sm text-center font-medium text-blue-500">
                  These cards don't rank against each other because they're different suits.
                </p>
              </motion.div>
            )}
            
            {currentPage === 3 && (
              <motion.div 
                key="page3"
                custom={direction}
                variants={pageVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="min-h-[280px]"
              >
                <h3 className="text-lg font-bold mb-4 text-center">Player Decks</h3>
                <p className="mb-4 text-sm text-center">
                  In a two-player match, each player receives 5 cards.
                </p>
                
                <div className="flex flex-col gap-6 mb-4">
                  {/* Player 1's deck */}
                  <div className="flex flex-col items-center">
                    <p className="text-sm font-semibold mb-2">Player 1's Cards</p>
                    <div className="flex gap-1 justify-center">
                      <img src={`${defaultDeckUrl}/A6.svg`} alt="A6" className="w-12 h-20" />
                      <img src={`${defaultDeckUrl}/A10.svg`} alt="A10" className="w-12 h-20" />
                      <img src={`${defaultDeckUrl}/A11.svg`} alt="A11" className="w-12 h-20" />
                      <img src={`${defaultDeckUrl}/A12.svg`} alt="A12" className="w-12 h-20" />
                      <img src={`${defaultDeckUrl}/A13.svg`} alt="A13" className="w-12 h-20" />
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <span className="text-xl font-bold text-yellow-500">VS</span>
                  </div>
                  
                  {/* Player 2's deck */}
                  <div className="flex flex-col items-center">
                    <p className="text-sm font-semibold mb-2">Player 2's Cards</p>
                    <div className="flex gap-1 justify-center">
                      <img src={`${defaultDeckUrl}/B6.svg`} alt="B6" className="w-12 h-20" />
                      <img src={`${defaultDeckUrl}/B10.svg`} alt="B10" className="w-12 h-20" />
                      <img src={`${defaultDeckUrl}/B11.svg`} alt="B11" className="w-12 h-20" />
                      <img src={`${defaultDeckUrl}/B12.svg`} alt="B12" className="w-12 h-20" />
                      <img src={`${defaultDeckUrl}/B13.svg`} alt="B13" className="w-12 h-20" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {currentPage === 4 && (
              <motion.div 
                key="page4"
                custom={direction}
                variants={pageVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="min-h-[280px]"
              >
                <h3 className="text-lg font-bold mb-3 text-center">Game Flow</h3>
                <div className="space-y-3 mb-4 text-sm">
                  <p>
                    <span className="text-primary font-semibold">1.</span> One player starts the game by playing a card of their choice.
                  </p>
                  <p>
                    <span className="text-primary font-semibold">2.</span> Whoever starts has the lead.
                  </p>
                  <p>
                    <span className="text-primary font-semibold">3.</span> The next player must play a card of the same suit if they have one.
                  </p>
                  <p>
                    <span className="text-primary font-semibold">4.</span> When all 10 cards are played (5 from each player), whoever is in the lead wins the round.
                  </p>
                </div>
                
                <div className="flex justify-center items-center gap-3 mb-3">
                  <div className="flex flex-col items-center">
                    <img src={`${defaultDeckUrl}/A10.svg`} alt="A10" className="w-16 h-24" />
                    <p className="text-xs mt-1">First player leads</p>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <img src={`${defaultDeckUrl}/A6.svg`} alt="A6" className="w-16 h-24" />
                    <p className="text-xs mt-1">Must follow suit</p>
                  </div>
                </div>
                
                <p className="text-center text-sm text-yellow-400">
                  Visit the Learn tab for more details on scoring!
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between mb-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={prevPage}
            disabled={currentPage === 0}
            className="w-24"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          
          <div className="flex gap-1">
            {[0, 1, 2, 3, 4].map((page) => (
              <div 
                key={page} 
                className={`w-2 h-2 rounded-full ${currentPage === page ? 'bg-primary' : 'bg-gray-600'}`}
              />
            ))}
          </div>
          
          <Button 
            variant="outline"
            size="sm"
            onClick={nextPage}
            disabled={currentPage === 4}
            className="w-24"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-white/10">
          {/* Keep the Practice Match and Skip Tutorial buttons consistent across all pages */}
          <Button
            variant="outline"
            onClick={onSkip}
            className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-900 order-2 sm:order-1"
          >
            Skip Tutorial
          </Button>
          
          <Button 
            onClick={onStartPractice} 
            className="flex-1 bg-primary hover:bg-primary/90 order-1 sm:order-2"
          >
            <Gamepad className="h-4 w-4 mr-2" />
            Play Practice Match
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}